import dayjs from 'dayjs'
import { AlertTriangle, ShieldAlert } from 'lucide-react'
import React from 'react'
import { useForm } from 'react-hook-form'
import { Dialog, GridHookForm } from 'vnetwork-ui'
import { getDaysInfo } from './career-path-constants'
import type { TransitionDialogProps } from './career-path-types'

const TransitionDialog: React.FC<TransitionDialogProps> = ({
    open,
    onClose,
    action,
    currentPath,
    isLoading,
    onSubmit,
    t,
}) => {
    const formControl = useForm({
        defaultValues: {
            transition_date: dayjs(),
            career_path_note: '',
        },
    })

    React.useEffect(() => {
        if (open) {
            formControl.reset({
                transition_date: dayjs(),
                career_path_note: '',
            })
        }
    }, [open, formControl])

    if (!action) return null

    // Warning when stage still has days remaining
    const daysInfo = currentPath
        ? getDaysInfo(currentPath.career_path_end_date)
        : null
    const hasEarlyWarning = daysInfo && !daysInfo.isOverdue

    // Warning when transitioning to offboard (account will be disabled)
    const isOffboarding = action.nextStage === 'offboard'

    return (
        <Dialog open={open} onClose={onClose} title={action.label} closeOnClick>
            {isOffboarding && (
                <div className="mb-4 flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800">
                    <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>{t('career_path.offboard_warning')}</span>
                </div>
            )}
            {hasEarlyWarning && (
                <div className="mb-4 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>
                        {t('career_path.early_transition_warning', {
                            days: daysInfo.days,
                        })}
                    </span>
                </div>
            )}
            <GridHookForm
                formControl={formControl}
                onSubmit={onSubmit}
                className="grid grid-cols-1 gap-4"
                data={[
                    {
                        formItemProps: {
                            name: 'transition_date',
                            label: t('career_path.transition_date'),
                            type: 'datepicker',
                            placeholder: t('career_path.transition_date'),
                        },
                    },
                    {
                        formItemProps: {
                            name: 'career_path_note',
                            label: t('career_path.note'),
                            type: 'textarea',
                            placeholder: t('career_path.note'),
                            rules: { required: t('field_required') },
                        },
                    },
                ]}
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        variant: 'outline',
                        handler: onClose,
                    },
                    {
                        title: action.label,
                        type: 'submit',
                        variant: 'default',
                        loading: isLoading,
                        disabled: isLoading,
                    },
                ]}
            />
        </Dialog>
    )
}

export default TransitionDialog
